'use client';

import type {DehydratedState, QueryClient} from '@tanstack/query-core';
import {hydrate} from '@tanstack/query-core';
import {useQueryClient} from '@tanstack/react-query';
import {PropsWithChildren, useMemo} from 'react';

const latestSeenServerTimestamps = new WeakMap<
    QueryClient,
    Map<string, number>
>();

interface HydrationBoundaryProps extends PropsWithChildren {
    state?: unknown;
    replacePrevious?: boolean;
}

/* This is a modified version of HydrationBoundary component that always
performs hydration synchronously even for already existing queries. Original
component queues hydration to run in effect after the first render, causing
useQuery to return outdated data, which is critical for query hooks likeuseMeQuery
or useIsAuth. */
export const QueryHydratorClient = ({
    children,
    state,
    replacePrevious,
}: HydrationBoundaryProps) => {
    const queryClient = useQueryClient();

    useMemo(() => {
        if (state) {
            if (typeof state !== 'object') {
                return;
            }

            const queryCache = queryClient.getQueryCache();
            const queries = (state as DehydratedState).queries || [];
            const newQueries: DehydratedState['queries'] = [];
            let timestamps = latestSeenServerTimestamps.get(queryClient);
            if (!timestamps) {
                timestamps = new Map();
                latestSeenServerTimestamps.set(queryClient, timestamps);
            }

            for (const dehydratedQuery of queries) {
                const existingQuery = queryCache.get(dehydratedQuery.queryHash);
                const previousTimestamp = timestamps.get(
                    dehydratedQuery.queryHash,
                );
                const isReplay =
                    previousTimestamp !== undefined &&
                    dehydratedQuery.state.dataUpdatedAt <= previousTimestamp;
                if (!isReplay) {
                    timestamps.set(
                        dehydratedQuery.queryHash,
                        dehydratedQuery.state.dataUpdatedAt,
                    );
                }
                if (existingQuery) {
                    if (!replacePrevious || isReplay) {
                        continue;
                    }
                    /* Not removing the already existing query from cache before
                    synchronous hydration causes a bug with layout.css files not
                    loading on client side navigation. It's a mystery how it's
                    related... */
                    queryCache.remove(existingQuery);
                }
                if (isReplay) {
                    newQueries.push({
                        ...dehydratedQuery,
                        state: {
                            ...dehydratedQuery.state,
                            isInvalidated: true,
                        },
                    });
                } else {
                    newQueries.push(dehydratedQuery);
                }
            }

            if (newQueries.length > 0) {
                hydrate(queryClient, {queries: newQueries});
            }
        }
    }, [queryClient, state, replacePrevious]);

    return children;
};
